import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Link } from 'react-router'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: '#06060E', color: '#F0EFF5' }}
      >
        <div className="text-center max-w-md">
          <div className="flex items-center justify-center gap-2.5 mb-8">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-extrabold"
              style={{ background: 'linear-gradient(135deg, #A78BFA, #7DD3FC)', color: '#06060E' }}
            >
              G
            </div>
            <span className="text-lg font-extrabold tracking-tight">GraspPod</span>
          </div>

          <h1 className="text-2xl font-extrabold mb-3">Something went wrong</h1>
          <p className="text-sm text-[#8B8A95] mb-6">
            An unexpected error occurred. Please try reloading the page.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <pre className="text-left text-xs text-[#55546A] bg-[#0E0E1A] border border-white/[0.06] rounded-xl p-4 mb-6 max-h-40 overflow-auto">
              {this.state.error.message}
              {this.state.error.stack && `\n\n${this.state.error.stack}`}
            </pre>
          )}

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: 'linear-gradient(135deg, #A78BFA, #7DD3FC)', color: '#06060E' }}
            >
              Reload
            </button>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-[#8B8A95] transition-colors hover:text-[#F0EFF5]"
              style={{ border: '1px solid rgba(255,255,255,0.06)' }}
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    )
  }
}
